import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ClipboardList, Layers, Search, ShieldCheck } from "lucide-react";
import { LANDING_CHECK_ICONS } from "../data/landingCheckIcons";
import { FALLBACK_LANDING_CHECKS } from "../data/landingChecksFallback";
import {
  LANDING_EXTRA_FEATURES,
  LANDING_FEATURED_CHECK,
} from "../data/landingChecksExtras";
import { LandingPricingSection } from "../components/LandingPricing/LandingPricingSection";
import { CHECK_NAME_LABELS } from "../utils/checkLabels";
import "./Landing.scss";

export const Landing = () => {
  const heroRef = useRef<HTMLDivElement>(null);
  const [spot, setSpot] = useState({ x: "50%", y: "38%" });
  const FeaturedCheckIcon = LANDING_CHECK_ICONS[LANDING_FEATURED_CHECK.iconKey];

  const onHeroMove = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    const el = heroRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const x = ((e.clientX - r.left) / r.width) * 100;
    const y = ((e.clientY - r.top) / r.height) * 100;
    setSpot({ x: `${x}%`, y: `${y}%` });
  }, []);

  useEffect(() => {
    const el = heroRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    setSpot({ x: "50%", y: `${((r.height * 0.38) / r.height) * 100}%` });
  }, []);

  useEffect(() => {
    const root = document.documentElement;
    root.classList.add("landing-page-scroll");
    return () => root.classList.remove("landing-page-scroll");
  }, []);

  return (
    <div className="landing">
      <nav className="landing-nav">
        <div className="landing-nav-inner">
          <a href="#top" className="landing-logo">
            I N A R I
          </a>
          <div className="landing-nav-links">
            <a href="#how">Как это работает</a>
            <a href="#checks">Проверки</a>
            <a href="#pricing">Тарифы</a>
            <a href="#contacts">Контакты</a>
          </div>
          <div className="landing-nav-actions">
            <Link to="/register" className="landing-btn-ghost landing-nav-register">
              Регистрация
            </Link>
            <Link to="/login" className="landing-btn-primary">
              Вход
            </Link>
          </div>
        </div>
      </nav>

      <div
        id="top"
        ref={heroRef}
        className="landing-hero-wrap"
        onMouseMove={onHeroMove}
      >
        <div
          className="landing-spotlight"
          aria-hidden
          style={{
            background: `radial-gradient(320px circle at ${spot.x} ${spot.y}, rgba(255,255,255,0.06) 0%, transparent 72%)`,
          }}
        />
        <div className="landing-hero-main">
          <div className="landing-container">
            <div className="landing-hero">
              <div className="landing-badge">
                <span className="landing-badge-dot" />
                Анализ веб-ресурсов
              </div>
              <h1 className="landing-hero-title">
                <span className="landing-hero-accent">I N A R I</span>
                <span className="landing-hero-subline">
                  отчёты по всем вашим доменам в одном месте
                </span>
              </h1>
              <p className="landing-hero-sub">
              Проверьте свой бизнес на соответствие законодательству о персональных данных
              </p>
              <div className="landing-hero-cta">
                <Link to="/login" className="landing-btn-primary">
                  Войти в систему
                </Link>
                <Link to="/register" className="landing-btn-ghost">
                  Создать аккаунт
                </Link>
              </div>
            </div>
          </div>
        </div>

        <div className="landing-container landing-hero-trust-bar">
          <div className="landing-trust">
            <div className="landing-trust-item">
              <ShieldCheck size={18} strokeWidth={1.5} />
              Авторизованный доступ
            </div>
            <div className="landing-trust-item">
              <Layers size={18} strokeWidth={1.5} />
              История проверок
            </div>
            <div className="landing-trust-item">
              <Search size={18} strokeWidth={1.5} />
              Детальные результаты
            </div>
          </div>
        </div>
      </div>

      <div className="landing-divider" />

      <section id="how" className="landing-section">
        <div className="landing-container">
          <div className="landing-section-head center">
            <p className="landing-eyebrow">Процесс</p>
            <h2 className="landing-section-title">Как это работает</h2>
            <p className="landing-section-sub">
              Три шага от заявки до отчёта — прозрачно и предсказуемо.
            </p>
          </div>
          <div className="landing-steps">
            <div className="landing-step">
              <span className="landing-step-num">01</span>
              <div className="landing-step-icon">
                <ClipboardList size={22} strokeWidth={1.5} />
              </div>
              <h3>Заявка</h3>
              <p>
                Укажите компанию, ИНН и URL — форма проверена и готова к отправке.
              </p>
            </div>
            <div className="landing-step">
              <span className="landing-step-num">02</span>
              <div className="landing-step-icon">
                <Search size={22} strokeWidth={1.5} />
              </div>
              <h3>Запуск</h3>
              <p>
                После планирования задача попадает в очередь; статус виден в списке.
              </p>
            </div>
            <div className="landing-step">
              <span className="landing-step-num">03</span>
              <div className="landing-step-icon">
                <Layers size={22} strokeWidth={1.5} />
              </div>
              <h3>Отчёт</h3>
              <p>
                Карточки проверок, таблица результатов и выгрузка при необходимости.
              </p>
            </div>
          </div>
        </div>
      </section>

      <div className="landing-divider" />

      <section id="checks" className="landing-section">
        <div className="landing-container">
          <div className="landing-checks-block landing-checks-block--standalone">
            <div className="landing-section-head center landing-checks-head">
              <p className="landing-eyebrow">Отчёт</p>
              <h2 className="landing-section-title">Проверки в системе</h2>
              <p className="landing-section-sub">
                Автоматические проверки сайта на соответствие требованиям — от реестра РКН до форм
                сбора данных.
              </p>
            </div>

            <div className="landing-card landing-check-card landing-check-card--featured">
              {FeaturedCheckIcon ? (
                <div className="landing-check-card-icon landing-check-card-icon--featured" aria-hidden>
                  <FeaturedCheckIcon size={28} strokeWidth={1.5} />
                </div>
              ) : null}
              <h3>{LANDING_FEATURED_CHECK.title}</h3>
              <p>{LANDING_FEATURED_CHECK.description}</p>
            </div>

            <div className="landing-cards landing-checks-cards">
              {FALLBACK_LANDING_CHECKS.map((c) => {
                const CheckIcon = LANDING_CHECK_ICONS[c.checkName];
                return (
                  <div key={c.checkName} className="landing-card landing-check-card">
                    {CheckIcon ? (
                      <div className="landing-check-card-icon" aria-hidden>
                        <CheckIcon size={22} strokeWidth={1.5} />
                      </div>
                    ) : null}
                    <h3>
                      {CHECK_NAME_LABELS[c.checkName] ??
                        c.checkNameDescription ??
                        c.checkName}
                    </h3>
                  </div>
                );
              })}
            </div>

            <div className="landing-cards landing-checks-extras">
              {LANDING_EXTRA_FEATURES.map((feature) => {
                const FeatureIcon = feature.icon;
                return (
                  <div key={feature.id} className="landing-card landing-check-card landing-check-card--extra">
                    <div className="landing-check-card-icon landing-check-card-icon--extra" aria-hidden>
                      <FeatureIcon size={22} strokeWidth={1.5} />
                    </div>
                    <h3>{feature.title}</h3>
                    <p>{feature.description}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      <div className="landing-divider" />

      <LandingPricingSection />

      <div className="landing-divider" />
    </div>
  );
};
